import React from "react";
import { Link } from "react-router-dom";

interface NavigationSubItem {
  name: string;
  href?: string;
  current?: boolean;
}

interface MobileNavigationItemProps {
  name: string;
  href?: string;
  current?: boolean;
  children?: NavigationSubItem[];
  isMenuOpen?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
}

const isExternalHref = (href: string): boolean => /^https?:\/\//.test(href);
const externalAccentClasses = `text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 inline-flex items-center gap-1`;
const ExternalLinkIcon: React.FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14 5h5v5m0-5L10 14m-4-4v9a1 1 0 001 1h9"
    />
  </svg>
);

const MobileNavigationItem: React.FC<MobileNavigationItemProps> = ({
  name,
  href,
  current,
  children,
  isMenuOpen,
  onToggle,
  onClose,
}) => {
  const baseButtonClasses = `rounded-md text-base font-medium transition-colors min-h-[44px]`;
  const activeClasses = `bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100`;
  const inactiveClasses = `text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white`;

  if (children != null) {
    return (
      <div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className={`w-full flex items-center justify-between px-3 py-3 ${baseButtonClasses} ${
            (current ?? false) ? activeClasses : inactiveClasses
          }`}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ?? false}
        >
          {name}
          <svg
            className={`h-4 w-4 transition-transform ${(isMenuOpen ?? false) ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {(isMenuOpen ?? false) && (
          <div className="mt-1 pl-4 space-y-1">
            {children.map((subItem) =>
              subItem.href != null && isExternalHref(subItem.href) ? (
                <a
                  key={subItem.name}
                  href={subItem.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block px-3 py-3 rounded-md text-base font-medium transition-colors min-h-[44px] ${externalAccentClasses} ${
                    (subItem.current ?? false) ? "bg-indigo-100 dark:bg-indigo-900/40" : ""
                  }`}
                  onClick={onClose}
                >
                  <span>{subItem.name}</span>
                  <ExternalLinkIcon />
                </a>
              ) : subItem.href != null ? (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  className={`block px-3 py-3 rounded-md text-base font-medium transition-colors min-h-[44px] ${
                    (subItem.current ?? false)
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                  }`}
                  onClick={onClose}
                >
                  {subItem.name}
                </Link>
              ) : (
                <span
                  key={subItem.name}
                  className={`block px-3 py-3 rounded-md text-base font-medium transition-colors min-h-[44px] ${
                    (subItem.current ?? false)
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                  }`}
                >
                  {subItem.name}
                </span>
              )
            )}
          </div>
        )}
      </div>
    );
  }

  return href != null && isExternalHref(href) ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`block px-3 py-3 ${baseButtonClasses} ${externalAccentClasses} ${
        (current ?? false) ? "bg-indigo-100 dark:bg-indigo-900/40" : ""
      }`}
      onClick={onClose}
    >
      <span>{name}</span>
      <ExternalLinkIcon />
    </a>
  ) : href != null ? (
    <Link
      to={href}
      className={`block px-3 py-3 ${baseButtonClasses} ${
        (current ?? false) ? activeClasses : inactiveClasses
      }`}
      onClick={onClose}
    >
      {name}
    </Link>
  ) : (
    <span
      className={`block px-3 py-3 ${baseButtonClasses} ${
        (current ?? false) ? activeClasses : inactiveClasses
      }`}
    >
      {name}
    </span>
  );
};

export default MobileNavigationItem;
